import fs from 'node:fs';
import { cancel, confirm, isCancel, select } from '@clack/prompts';
import color from 'chalk';
import { Argument, Command, program } from 'commander';
import { execa } from 'execa';
import { resolveCommand } from 'package-manager-detector/commands';
import { detect } from 'package-manager-detector/detect';
import path from 'pathe';
import * as v from 'valibot';
import {
	DEPS_FOR_TESTING,
	DEV_DEPS_FOR_TESTING,
	NGM_DEV_CLI_NAME,
	NGM_DEV_CLI_NAME_FOR_FILES
} from '../constants';
import * as ascii from '../utils/ascii';
import { resolveTree } from '../utils/blocks';
import * as url from '../utils/blocks/ts/url';
import { isTestFile } from '../utils/build';
import { type ProjectConfig, getProjectConfig, resolvePaths } from '../utils/config';
import {
	type ConcurrentTask,
	intro,
	promptInstallDependencies,
	runTasksConcurrently,
	spinner
} from '../utils/prompts';
import * as registry from '../utils/registry-providers/internal';
import { getAngularVersion } from '../utils/angular-version';

const schema = v.objectWithRest(
	{
		repo: v.optional(v.string()),
		allow: v.boolean(),
		// cache: v.boolean(),
		verbose: v.boolean(),
		cwd: v.string(),
		angularVersion: v.string()
	},
	v.unknown()
);

type Options = v.InferInput<typeof schema>;

const exec = new Command('exec')
	.alias('x')
	.description('Execute a block as a script.')
	.addArgument(
		new Argument(
			'script',
			'Name of the script you want to execute. ex: (general/hello)'
		).argOptional()
	)
	// .option('--repo <repo>', 'Repository to download and run the script from.')
	.option(
		'-A, --allow',
		`Allow ${NGM_DEV_CLI_NAME} to download code from the provided repo.`,
		false
	)
	// .option('--no-cache', 'Disable caching of resolved git urls.')
	.option('--verbose', 'Include debug logs.', false)
	.option('--cwd [path]', 'The current working directory.', process.cwd())
	.option(
		'--angular-version [version]',
		'Angular version to be used for the block generation. If not provided, it will be inferred from the project. Supported versions are: 19, 20, 21.'
	)
	.allowExcessArguments()
	.allowUnknownOption()
	.action(async (script, opts, command) => {
		const options = v.parse(schema, opts);

		await intro();

		await _exec(script, options, command);
	});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _exec = async (s: string | undefined, options: Options, command: any) => {
	const verbose = (msg: string) => {
		if (options.verbose) {
			console.info(`${ascii.INFO} ${msg}`);
		}
	};
	const angularVersion = getAngularVersion({
		cwd: options.cwd,
		angularVersion: options.angularVersion,
		verbose
	});

	let script = s;

	const loading = spinner({ verbose: options.verbose ? verbose : undefined });

	const configResult = getProjectConfig(options.cwd);

	/** The user has opted for no config */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _noConfig = configResult.isErr();

	let config: ProjectConfig;

	if (configResult.isErr()) {
		// add default config used for default values in prompts
		config = {
			$schema: '',
			includeTests: false,
			watermark: true,
			paths: {
				'*': './'
			},
			repos: []
		};
	} else {
		config = configResult.unwrap();
	}

	let repoPaths = config.repos;

	// we just want to override all others if supplied via the CLI
	// if (options.repo) repoPaths = [options.repo];

	const provider = script ? registry.selectProvider(script) : undefined;

	// we are only getting repos for blocks that specified repos
	if (script && provider) {
		const { url: repo } = provider.parse(script, { fullyQualified: true });

		if (!repoPaths.find((repoPath) => repoPath === repo)) {
			if (!options.allow) {
				const result = await confirm({
					message: `Allow ${ascii.NGM_DEV_CLI} to download and run code from ${color.cyan(repo)}?`,
					initialValue: true
				});

				if (isCancel(result) || !result) {
					cancel('Canceled!');
					process.exit(0);
				}
			}

			repoPaths = [repo];
		}
	}

	if (!options.allow && options.repo) {
		const result = await confirm({
			message: `Allow ${ascii.NGM_DEV_CLI} to download and run code from ${color.cyan(options.repo)}?`,
			initialValue: true
		});

		if (isCancel(result) || !result) {
			cancel('Canceled!');
			process.exit(0);
		}
	}

	if (repoPaths.length === 0) {
		// if (noConfig) {
		// 	program.error(
		// 		color.red(
		// 			`Fully quality your script ex: (github/ieedan/std/scripts/build) or provide the \`${color.bold(
		// 				'--repo'
		// 			)}\` flag to specify a registry.`
		// 		)
		// 	);
		// }

		program.error(
			color.red(
				`There were no repos present in your config. Please run \`${color.bold(
					`${NGM_DEV_CLI_NAME} init`
				)}\` to initialize your project.`
			)
		);
	}

	loading.start(`Fetching scripts from ${color.cyan(repoPaths.join(', '))}`);

	const resolvedRepos: registry.RegistryProviderState[] = (
		await registry.forEachPathGetProviderState(repoPaths, { noCache: true })
	).match(
		(val) => val,
		({ repo, message }) => {
			loading.stop(`Failed to get info for ${color.cyan(repo)}`);
			program.error(color.red(message));
		}
	);

	const blocksMap = (
		await registry.fetchBlocks({ repos: resolvedRepos, version: angularVersion })
	).match(
		(val) => val,
		({ repo, message }) => {
			loading.stop(`Failed fetching scripts from ${color.cyan(repo)}`);
			program.error(color.red(message));
		}
	);

	loading.stop(`Retrieved scripts from ${color.cyan(repoPaths.join(', '))}`);

	// if no blocks are provided prompt the user for what blocks they want
	if (!script) {
		const promptResult = await select({
			message: 'Select which script to run.',
			options: Array.from(blocksMap.entries())
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				.filter(([_, value]) => value.list)
				.map(([key, value]) => {
					let label: string;

					// show the full repo if there are multiple repos
					if (repoPaths.length > 1) {
						label = `${color.cyan(url.join(value.sourceRepo.url, value.category))}/${value.name}`;
					} else {
						label = `${color.cyan(value.category)}/${value.name}`;
					}

					return {
						label: label,
						value: key
					};
				})
		});

		if (isCancel(promptResult)) {
			cancel('Canceled!');
			process.exit(0);
		}

		script = promptResult as string;
	}

	const resolveResult = await resolveTree([script], blocksMap, resolvedRepos);
	if (resolveResult.isErr()) {
		program.error(resolveResult.unwrapErr());
		return; // This won't be reached, but helps TypeScript
	}
	const installingBlocks = resolveResult.unwrap();

	const tempDirBase = `temp-${NGM_DEV_CLI_NAME_FOR_FILES}-exec`;

	const tempDirectoryRelative = `./${tempDirBase}/${encodeURIComponent(script)}`;

	const tempDirectory = path.join(process.cwd(), tempDirectoryRelative);

	config.paths['*'] = tempDirectoryRelative;

	fs.mkdirSync(tempDirectory, { recursive: true });

	const pm = (await detect({ cwd: process.cwd() }))?.agent ?? 'npm';

	const tasks: ConcurrentTask[] = [];

	const devDeps: Set<string> = new Set<string>();
	const deps: Set<string> = new Set<string>();

	const resolvedPathsResult = resolvePaths(config.paths, options.cwd);

	if (resolvedPathsResult.isErr()) {
		program.error(color.red(resolvedPathsResult.unwrapErr()));
	}

	const resolvedPaths = resolvedPathsResult.unwrap();

	const addedBlocks: string[] = [];

	for (const block of installingBlocks) {
		const fullSpecifier = `${block.sourceRepo.url}/${block.category}/${block.name}`;
		const shortSpecifier = `${block.category}/${block.name}`;

		const providerInfo = block.sourceRepo;

		const directory = path.join(options.cwd, resolvedPaths['*'], block.category);

		addedBlocks.push(shortSpecifier);

		tasks.push({
			run: async ({ message }) => {
				message(`Adding ${color.cyan(fullSpecifier)}`);

				// in case the directory didn't already exist
				fs.mkdirSync(directory, { recursive: true });

				const files: { content: string; destPath: string }[] = [];

				const getSourceFile = async (filePath: string) => {
					const content = await registry.fetchRaw(providerInfo, filePath, {
						verbose,
						version: angularVersion
					});

					if (content.isErr()) {
						loading.stop(color.red(`Error fetching ${color.bold(filePath)}`));
						program.error(color.red(`There was an error trying to get ${fullSpecifier}`));
					}

					return content.unwrap();
				};

				for (const sourceFile of block.files) {
					if (!config.includeTests && isTestFile(sourceFile)) continue;

					const sourcePath = path.join(block.directory, sourceFile);

					let destPath: string;
					if (block.subdirectory) {
						destPath = path.join(directory, block.name, sourceFile);
					} else {
						destPath = path.join(directory, sourceFile);
					}

					const content = await getSourceFile(sourcePath);

					const pathFolder = destPath.slice(0, destPath.length - sourceFile.length);

					fs.mkdirSync(pathFolder, {
						recursive: true
					});

					files.push({ content, destPath });
				}

				for (const file of files) {
					fs.writeFileSync(file.destPath, file.content);
				}

				if (config.includeTests && block.tests) {
					const { devDependencies, dependencies } = JSON.parse(
						fs.readFileSync(path.join(options.cwd, 'package.json')).toString()
					);

					if (devDependencies === undefined) {
						DEV_DEPS_FOR_TESTING.forEach((dep) => devDeps.add(dep));
					}

					if (dependencies === undefined) {
						DEPS_FOR_TESTING.forEach((dep) => deps.add(dep));
					}
				}

				for (const dep of block.devDependencies) {
					devDeps.add(dep);
				}

				for (const dep of block.dependencies) {
					deps.add(dep);
				}
			}
		});
	}

	await runTasksConcurrently({
		startMessage: 'Adding blocks',
		stopMessage: `Added ${color.cyan(addedBlocks.join(', '))}`,
		loading,
		tasks
	});

	const hasDependencies = deps.size > 0 || devDeps.size > 0;

	// create temporary package.json
	if (hasDependencies) {
		// add package.json
		const packageContent = {
			name: 'temp-package',
			type: 'module',
			version: '0.0.1'
		};

		const packagePath = path.join(tempDirectory, 'package.json');

		fs.writeFileSync(packagePath, JSON.stringify(packageContent, null, '\t'));
	}

	await promptInstallDependencies(deps, devDeps, {
		yes: true,
		no: false,
		cwd: tempDirectory,
		pm,
		ignoreWorkspace: true
	});

	const startIndex = (command.parent.rawArgs as string[]).findIndex((arg) => arg === '--');

	let passthroughArgs: string[] = [];

	if (startIndex !== -1) {
		passthroughArgs = command.parent.rawArgs.slice(startIndex + 1);
	}

	verbose(`Passing args ${color.cyan(passthroughArgs.join(' '))}`);

	// run the cli

	console.clear();

	const runningBlock = installingBlocks[0];

	let file: string;

	// tsx seems to be smart enough to figure out if it is a .ts file
	if (runningBlock.subdirectory) {
		file = path.join(tempDirectory, `${runningBlock.category}/${runningBlock.name}/index.js`);
	} else {
		file = path.join(tempDirectory, `${runningBlock.category}/${runningBlock.name}.js`);
	}

	const cmd = resolveCommand(pm, 'execute', ['tsx', file, ...passthroughArgs]);

	if (!cmd) {
		program.error(color.red('Error resolving run command!'));
	}

	try {
		await execa(cmd.command, cmd.args, {
			cwd: process.cwd(),
			stdin: process.stdin,
			stdout: process.stdout
		});
	} finally {
		fs.rmSync(path.join(process.cwd(), tempDirBase), { recursive: true, force: true });
	}
};

export { exec, _exec };
